import React, { useState } from 'react';
import { Plus, X, Video, Calendar, Clock, Users, ExternalLink, Edit2, Trash2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import './AdminPages.css';

const PLATFORMS = { 'Google Meet':'#34a853','Zoom':'#2d8cff','Microsoft Teams':'#6264a7' };
const emptyForm = { title:'', date:'', time:'', platform:'Google Meet', description:'', link:'', participants:[] };

export default function MeetingsPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder, user } = useAuth();

  const canSchedule = can('canScheduleMeeting');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    if (!form.title || !form.date) { showToast('Title and date required','error'); return; }
    const selfId = user?.employeeId || user?.uid;
    try {
      if (editTarget) {
        await commitData('meetings', data.meetings.map(m=>m.id===editTarget.id?{...m,...form}:m));
        showToast('Meeting updated — participants notified by email when mail is configured');
      } else {
        await commitData('meetings', [...data.meetings,{ id:String(Date.now()),...form,participants:selfId?[selfId]:[] }]);
        showToast('Meeting scheduled — participants emailed when mail is configured');
      }
      setShowModal(false); setEditTarget(null); setForm(emptyForm);
    } catch {}
  };

  const openEdit = (m) => {
    setEditTarget(m); setForm({ title:m.title,date:m.date,time:m.time,platform:m.platform,description:m.description||'',link:m.link,participants:m.participants||[] });
    setShowModal(true);
  };

  const deleteMeeting = async (id) => {
    if(!window.confirm('Delete this meeting?'))return;
    try {
      await commitData('meetings', data.meetings.filter(m=>m.id!==id));
      showToast('Meeting cancelled — participants emailed when mail is configured','warning');
    } catch {}
  };

  const getEmpName = (id) => data.employees.find(e=>String(e.id)===String(id))?.name || `User #${id}`;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div><h1 className="page-title">Meetings</h1><p className="page-subtitle">{data.meetings.length} scheduled</p></div>
        <div className="page-actions">
          {canSchedule && <button className="btn-gold" onClick={()=>{setEditTarget(null);setForm(emptyForm);setShowModal(true)}}><Plus size={15}/> Schedule Meeting</button>}
        </div>
      </div>

      <div className="two-col">
        {/* Meeting list */}
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          {data.meetings.map(m=>{
            const pc = PLATFORMS[m.platform]||'var(--secondary-color)';
            return (
              <div key={m.id} className="card" style={{ padding:20 }}>
                <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:12 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                    <div style={{ width:44,height:44,borderRadius:'var(--radius-md)',background:`${pc}20`,border:`1px solid ${pc}40`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Video size={18} style={{ color:pc }}/>
                    </div>
                    <div>
                      <p style={{ fontWeight:700,fontSize:'0.95rem' }}>{m.title}</p>
                      <p style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>{m.platform}</p>
                    </div>
                  </div>
                  <div style={{ display:'flex',gap:6 }}>
                    {canSchedule && <button className="btn-ghost" style={{ padding:'5px' }} onClick={()=>openEdit(m)}><Edit2 size={12}/></button>}
                    {isFounder   && <button className="btn-ghost" style={{ padding:'5px',color:'var(--danger)' }} onClick={()=>deleteMeeting(m.id)}><Trash2 size={12}/></button>}
                  </div>
                </div>
                <div style={{ display:'flex',gap:16,marginBottom:10,flexWrap:'wrap' }}>
                  <span style={{ display:'flex',alignItems:'center',gap:5,fontSize:'0.8rem',color:'var(--text-muted)' }}>
                    <Calendar size={12}/>{new Date(m.date).toLocaleDateString('en-ZA',{weekday:'short',day:'numeric',month:'long'})}
                  </span>
                  <span style={{ display:'flex',alignItems:'center',gap:5,fontSize:'0.8rem',color:'var(--text-muted)' }}>
                    <Clock size={12}/>{m.time}
                  </span>
                  <span style={{ display:'flex',alignItems:'center',gap:5,fontSize:'0.8rem',color:'var(--text-muted)' }}>
                    <Users size={12}/>{(m.participants||[]).length} participants
                  </span>
                </div>
                {m.description && <p style={{ fontSize:'0.8rem',color:'var(--text-muted)',marginBottom:12,lineHeight:1.5 }}>{m.description}</p>}
                {/* Participants */}
                <div style={{ display:'flex',gap:4,marginBottom:14,flexWrap:'wrap' }}>
                  {(m.participants||[]).slice(0,4).map(id=>(
                    <span key={id} style={{ background:'var(--surface-2)',border:'1px solid var(--border-color)',borderRadius:20,padding:'3px 10px',fontSize:'0.72rem',color:'var(--text-muted)' }}>{getEmpName(id)}</span>
                  ))}
                  {(m.participants||[]).length>4 && <span style={{ fontSize:'0.72rem',color:'var(--text-dim)' }}>+{m.participants.length-4} more</span>}
                </div>
                <a href={m.link||'#'} target="_blank" rel="noreferrer"
                  className="btn-gold"
                  style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,textDecoration:'none',padding:9,fontSize:'0.82rem',width:'100%' }}
                  onClick={e=>{ if(!m.link){e.preventDefault();showToast('No meeting link set','warning');} }}>
                  <Video size={14}/> Join {m.platform} <ExternalLink size={12}/>
                </a>
              </div>
            );
          })}
          {data.meetings.length===0 && <div style={{ textAlign:'center',padding:48,color:'var(--text-dim)' }}><Calendar size={36} style={{ marginBottom:12,opacity:0.3 }}/><p>No meetings scheduled</p></div>}
        </div>

        {/* Mini calendar */}
        <div>
          <div className="card" style={{ marginBottom:14 }}>
            <p className="card-title" style={{ marginBottom:16 }}>March 2025</p>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3,textAlign:'center' }}>
              {['M','T','W','T','F','S','S'].map((d,i)=>(
                <div key={i} style={{ fontSize:'0.62rem',fontWeight:700,color:'var(--text-dim)',padding:'3px 0' }}>{d}</div>
              ))}
              {[null,null,null,null,null,null,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31].map((day,i)=>{
                const isToday = day===17;
                const hasEvent = day && data.meetings.some(m=>new Date(m.date).getDate()===day);
                return (
                  <div key={i} style={{ padding:'5px 2px',borderRadius:5,fontSize:'0.78rem',fontWeight:isToday||hasEvent?700:400,background:isToday?'var(--secondary-color)':hasEvent?'rgba(201,162,39,0.12)':'transparent',color:isToday?'#000':hasEvent?'var(--secondary-color)':day?'var(--text-muted)':'transparent',cursor:day?'pointer':'default',position:'relative' }}>
                    {day}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <p className="card-title" style={{ marginBottom:12 }}>Platforms</p>
            {Object.entries(PLATFORMS).map(([name,color])=>(
              <div key={name} style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border-color)' }}>
                <div style={{ width:10,height:10,borderRadius:'50%',background:color,flexShrink:0 }}/>
                <span style={{ fontSize:'0.85rem',fontWeight:600,flex:1 }}>{name}</span>
                <span style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>{data.meetings.filter(m=>m.platform===name).length} meetings</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{editTarget?'Edit Meeting':'Schedule Meeting'}</p>
              <button className="modal-close" onClick={()=>setShowModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Meeting title"/></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/></div>
                <div className="form-group"><label className="form-label">Time</label><input className="form-input" type="time" value={form.time} onChange={e=>setForm({...form,time:e.target.value})}/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Platform</label>
                  <select className="form-input" value={form.platform} onChange={e=>setForm({...form,platform:e.target.value})}>
                    <option>Google Meet</option><option>Zoom</option><option>Microsoft Teams</option>
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Meeting Link</label><input className="form-input" value={form.link} onChange={e=>setForm({...form,link:e.target.value})} placeholder="https://..."/></div>
              </div>
              <div className="form-group"><label className="form-label">Description / Agenda</label><textarea className="form-input" rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Meeting agenda..."/></div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn-gold" onClick={save}>{editTarget?'Save Changes':'Schedule Meeting'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
